import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { BrowserQRCodeReader } from "@zxing/browser";
import { parseTicketFromPayload } from "../utility/ticketScanDecoder";
import "./LiveTicketScannerModal.css";

// Uses react modal lifecycle to start/stop the scanner when the modal opens/closes, which should be more reliable across devices and browsers than manually starting/stopping based on button clicks and other events
const LiveTicketScannerModal = ({ isOpen, toggle, onDetected }) => {
    const videoRef = useRef(null);
    const readerRef = useRef(null);
    const controlsRef = useRef(null);
    const onDetectedRef = useRef(onDetected);
    const toggleRef = useRef(toggle);

    const [scanError, setScanError] = useState("");
    const [isStarting, setIsStarting] = useState(false);

    if (!readerRef.current) {
        readerRef.current = new BrowserQRCodeReader();
    }

    useEffect(() => {
        onDetectedRef.current = onDetected;
        toggleRef.current = toggle;
    }, [onDetected, toggle]);

    function stopScanner() {
        try {
            if (controlsRef.current) {
                controlsRef.current.stop();
                controlsRef.current = null;
            }
            if (readerRef.current) {
                readerRef.current.reset();
            }
        } catch (err) {
            // no-op cleanup
        }
    }

    async function startScanner() {
        if (!videoRef.current) {
            setScanError("Camera element not ready. Close and try again.");
            return;
        }

        setIsStarting(true);
        setScanError("");

        try {
            controlsRef.current = await readerRef.current.decodeFromConstraints(
                {
                    video: { facingMode: { ideal: "environment" } },
                },
                videoRef.current,
                (result) => {
                    if (!result) return;

                    const ticket = parseTicketFromPayload(result.getText());
                    if (!ticket) return;

                    stopScanner();
                    if (onDetectedRef.current) onDetectedRef.current(ticket);
                    toggleRef.current();
                },
            );
        } catch (err) {
            setScanError("Camera unavailable or permission denied. Try manual input.");
        } finally {
            setIsStarting(false);
        }
    }

    function handleOpened() {
        startScanner();
    }

    function handleCloseClick() {
        stopScanner();
        toggle();
    }

    function handleClosed() {
        stopScanner();
        setIsStarting(false);
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleCloseClick}
            onOpened={handleOpened}
            onClosed={handleClosed}
            centered
            fade={false}
            className="live-scan-modal">
            <ModalHeader toggle={handleCloseClick}>Live Ticket Scan</ModalHeader>
            <ModalBody>
                <div className="live-scan-video-wrap">
                    <video
                        ref={videoRef}
                        className="live-scan-video"
                        muted
                        playsInline
                        autoPlay
                    />
                    <div className="live-scan-guide">Align QR code inside frame</div>
                </div>

                {isStarting ? <div className="live-scan-msg">Starting camera...</div> : null}
                {scanError ? <div className="live-scan-error">{scanError}</div> : null}
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    onClick={handleCloseClick}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default LiveTicketScannerModal;
